/**
 * Admin Doctor Verification page — review and approve/reject doctor registrations.
 */
import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';

const VerifyDoctors = () => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await adminAPI.getPendingDoctors();
        setDoctors(Array.isArray(res.data) ? res.data : (res.data?.pending_doctors || []));
      } catch {
        toast.error('Failed to load pending doctors.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleVerify = async (doctorId, action) => {
    setProcessing(doctorId);
    try {
      await adminAPI.verifyDoctor({ doctor_uid: doctorId, action });
      setDoctors(prev => prev.filter(d => d.uid !== doctorId && d.id !== doctorId));
      toast.success(`Doctor ${action === 'approve' ? 'approved' : 'rejected'} successfully.`);
    } catch (err) {
      toast.error(typeof err === 'string' ? err : (err?.error || err?.message || 'Verification action failed.'));
    } finally {
      setProcessing(null);
    }
  };

  return (
    <Layout title="Verify Doctors" role="admin" showBack>
      <div className="p-4 pb-24">
        <p className="text-sm text-gray-500 mb-4">{doctors.length} doctor{doctors.length !== 1 ? 's' : ''} awaiting verification</p>

        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="skeleton h-32 rounded-2xl" />)}
          </div>
        ) : doctors.length === 0 ? (
          <div className="card text-center py-12 text-gray-400">
            <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <p className="font-medium">All caught up!</p>
            <p className="text-sm mt-1">No doctors awaiting verification.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {doctors.map(doc => {
              const doctorId = doc.uid || doc.id;

              return (
              <div key={doctorId} className="card">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-amber-700 font-bold text-lg">{(doc.full_name || 'D').charAt(0)}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">Dr. {doc.full_name}</h3>
                    <p className="text-sm text-gray-500">{doc.email}</p>
                  </div>
                  <span className="badge-warning text-xs">Pending</span>
                </div>

                <div className="bg-gray-50 rounded-xl p-3 space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-gray-500">License Number</span>
                    <span className="font-medium text-gray-900">{doc.license_number || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Specialization</span>
                    <span className="font-medium text-gray-900">{doc.specialization || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Experience</span>
                    <span className="font-medium text-gray-900">{doc.years_of_experience || 0} years</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">County</span>
                    <span className="font-medium text-gray-900">{doc.county || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Hospital</span>
                    <span className="font-medium text-gray-900">{doc.hospital_affiliation || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Phone</span>
                    <span className="font-medium text-gray-900">{doc.phone_number || '—'}</span>
                  </div>
                </div>

                {doc.bio && (
                  <p className="text-sm text-gray-600 mb-4 italic">"{doc.bio}"</p>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => handleVerify(doctorId, 'reject')}
                    disabled={processing === doctorId}
                    className="flex-1 py-2.5 rounded-xl font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors border border-red-200"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => handleVerify(doctorId, 'approve')}
                    disabled={processing === doctorId}
                    className="flex-1 py-2.5 rounded-xl font-medium text-white bg-accent-500 hover:bg-accent-600 transition-colors flex items-center justify-center gap-2"
                  >
                    {processing === doctorId && <div className="spinner w-4 h-4 border-2"></div>}
                    Approve
                  </button>
                </div>
              </div>
            )})}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default VerifyDoctors;
